import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';

/**
 * SubServiceGrid — used on parent service hub pages (Plumbing, Handyman, AC,
 * Refrigeration) to surface the full list of sub-services they offer.
 *
 * Each item can optionally have an `href` pointing to a dedicated sub-service
 * page; items without an href render as a styled tile without a link
 * (preventing 404s on sub-services that don't yet have their own page).
 *
 * Props:
 *   title         — heading above the grid (default "Our Services")
 *   subtitle      — supporting paragraph
 *   items         — [{ label, blurb?, href? }]
 */
const SubServiceGrid = ({ title = 'Our Services', subtitle, items = [] }) => {
  if (!items.length) return null;

  return (
    <section className="not-prose" data-testid="sub-service-grid">
      <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2">{title}</h2>
      {subtitle && <p className="text-gray-400 mb-8 max-w-3xl">{subtitle}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((item) => {
          const inner = (
            <>
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-white font-semibold text-base leading-snug">
                  {item.label}
                </h3>
                {item.href ? (
                  <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-amber-500 shrink-0 transition-colors" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-700 shrink-0" />
                )}
              </div>
              {item.blurb && (
                <p className="text-gray-400 text-sm mt-2 leading-relaxed">{item.blurb}</p>
              )}
            </>
          );

          if (item.href) {
            return (
              <Link
                key={item.label}
                to={item.href}
                className="group block rounded-lg border border-gray-800 hover:border-amber-500/50 bg-gray-900/40 hover:bg-gray-900/70 p-4 transition-colors"
                data-testid={`sub-service-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                {inner}
              </Link>
            );
          }
          return (
            <div
              key={item.label}
              className="rounded-lg border border-gray-800 bg-gray-900/30 p-4"
              data-testid={`sub-service-static-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              {inner}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default SubServiceGrid;
